// File: components/renderer/allies/drawBobby.ts
import { CostumeId } from "../../../types";

export const drawBobby = (ctx: CanvasRenderingContext2D, producerName: string, costume: CostumeId = 'DEFAULT') => {
    ctx.save();
    
    // Animation constants
    const time = Date.now();
    const breath = Math.sin(time / 400) * 1.2; 
    const bagSway = Math.sin(time / 300) * 0.04;

    // Palette per specification
    const JACKET_COLOR = "#94a3b8"; 
    const PANTS_COLOR = "#94a3b8";  
    const SHIRT_COLOR = "#ffffff";  
    const SKIN_TONE = "#f3d2b3";    
    const HAIR_COLOR = "#1a1311";   
    const LANYARD_COLOR = "#8b5cf6"; // Vivid Purple for visibility
    
    // BUILD ADJUSTMENT: Short and pudgy
    ctx.scale(1.18, 0.92);

    // --- 1. HIGH-TOP SNEAKERS ---
    const drawSneaker = (x: number, scaleX: number) => {
        ctx.save();
        ctx.translate(x, -5);
        ctx.scale(scaleX, 1);
        
        ctx.fillStyle = "#78350f";
        ctx.beginPath(); ctx.roundRect(-15, 0, 35, 8, 2); ctx.fill();

        ctx.fillStyle = "#ffffff";
        ctx.beginPath();
        ctx.moveTo(-15, 0);
        ctx.lineTo(20, 0);
        ctx.lineTo(18, -25); 
        ctx.quadraticCurveTo(10, -35, -5, -35);
        ctx.lineTo(-15, -15);
        ctx.closePath();
        ctx.fill();

        ctx.strokeStyle = "#ddd6fe"; 
        ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(-14, -2); ctx.lineTo(19, -2); ctx.stroke();
        ctx.restore();
    };

    drawSneaker(-28, 1); 
    drawSneaker(28, -1);

    // --- 2. LEGS (PUDGY) ---
    const drawLeg = (x: number, mirror: boolean) => {
        ctx.save();
        ctx.translate(x, -80);
        if(mirror) ctx.scale(-1, 1);
        
        ctx.fillStyle = PANTS_COLOR;
        ctx.beginPath();
        ctx.moveTo(-20, 0); 
        ctx.lineTo(20, 0); 
        ctx.quadraticCurveTo(24, 40, 16, 75);
        ctx.lineTo(-12, 75);
        ctx.quadraticCurveTo(-16, 40, -12, 0);
        ctx.fill();
        ctx.restore();
    };
    drawLeg(-18, false);
    drawLeg(18, true);

    // --- 3. TORSO (PUDGY BUILD) ---
    ctx.translate(0, breath);
    
    // T-Shirt
    ctx.fillStyle = SHIRT_COLOR;
    ctx.beginPath(); 
    ctx.ellipse(0, -130, 45, 58, 0, 0, Math.PI * 2); 
    ctx.fill();

    // T-Shirt Graphic (HUNTR/X Merch)
    ctx.fillStyle = "#6366f1";
    ctx.font = "bold 6px Arial";
    ctx.textAlign = "center";
    ctx.fillText("HUNTR/X", 0, -125);
    ctx.strokeStyle = "rgba(99, 102, 241, 0.3)";
    ctx.lineWidth = 1;
    ctx.strokeRect(-12, -135, 24, 15);

    // Open Gray Jacket
    ctx.fillStyle = JACKET_COLOR;
    ctx.beginPath();
    ctx.moveTo(-48, -165); 
    ctx.quadraticCurveTo(-58, -130, -45, -80);
    ctx.lineTo(-18, -80);
    ctx.quadraticCurveTo(-28, -130, -22, -165);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(48, -165); 
    ctx.quadraticCurveTo(58, -130, 45, -80);
    ctx.lineTo(18, -80);
    ctx.quadraticCurveTo(28, -130, 22, -165);
    ctx.fill();

    // --- 4. HIGH-VISIBILITY VIP LANYARD & BADGE ---
    ctx.strokeStyle = LANYARD_COLOR;
    ctx.lineWidth = 4; // Increased thickness for better visibility
    ctx.beginPath();
    ctx.moveTo(-20, -168); // Widened neck anchor
    ctx.quadraticCurveTo(0, -100, 20, -168); // Hangs lower on the chest
    ctx.stroke();
    
    // VIP ID Card (The Badge)
    ctx.save();
    ctx.translate(0, -118); 
    ctx.rotate(Math.sin(time / 400) * 0.12);
    
    // Card Shadow & Body
    ctx.fillStyle = "#ffffff";
    ctx.shadowBlur = 10; ctx.shadowColor = "rgba(0,0,0,0.4)";
    ctx.beginPath(); ctx.roundRect(-12, 0, 24, 34, 3); ctx.fill();
    ctx.shadowBlur = 0;
    
    // Card Top / Lanyard Clip Area
    ctx.fillStyle = LANYARD_COLOR;
    ctx.fillRect(-12, 0, 24, 8);
    
    // "VIP" Text on Badge
    ctx.fillStyle = "#fbbf24"; // Bright Gold
    ctx.font = "bold 8px 'Orbitron', sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("VIP", 0, 7);
    
    // Badge Details (Photo & ID info lines)
    ctx.fillStyle = "#cbd5e1";
    ctx.fillRect(-8, 12, 8, 9); // "Photo" area
    ctx.fillRect(2, 13, 7, 2);  // Text line 1
    ctx.fillRect(2, 17, 7, 2);  // Text line 2
    ctx.fillRect(2, 21, 5, 2);  // Text line 3
    
    // Holographic Strip at bottom
    const holoGrad = ctx.createLinearGradient(-12, 0, 12, 0);
    holoGrad.addColorStop(0, "#d946ef"); holoGrad.addColorStop(0.5, "#0ea5e9"); holoGrad.addColorStop(1, "#d946ef");
    ctx.fillStyle = holoGrad;
    ctx.globalAlpha = 0.6;
    ctx.fillRect(-12, 26, 24, 5);
    ctx.globalAlpha = 1.0;
    
    ctx.restore();

    // --- 5. ARMS ---
    const drawArm = (x: number, scaleX: number) => {
        ctx.save();
        ctx.translate(x, -155);
        ctx.scale(scaleX, 1);
        ctx.rotate(0.25);
        ctx.fillStyle = JACKET_COLOR;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.quadraticCurveTo(14, 25, 12, 50);
        ctx.lineTo(-12, 50);
        ctx.quadraticCurveTo(-10, 25, -18, 0);
        ctx.fill();
        ctx.fillStyle = SKIN_TONE;
        ctx.beginPath(); ctx.arc(0, 55, 9, 0, Math.PI*2); ctx.fill();
        ctx.restore();
    };

    drawArm(-45, 1);
    drawArm(45, -1);

    // --- 6. HEAD (MATCHING REFERENCE IMAGE) ---
    ctx.translate(0, -165);
    
    // Neck
    ctx.fillStyle = SKIN_TONE;
    ctx.fillRect(-10, -5, 20, 12);

    // Face Shape (Pudgy & Circular)
    ctx.beginPath();
    ctx.moveTo(-24, -30);
    ctx.quadraticCurveTo(-26, -5, -12, 16);
    ctx.quadraticCurveTo(0, 22, 12, 16);
    ctx.quadraticCurveTo(26, -5, 24, -30);
    ctx.lineTo(24, -50);
    ctx.lineTo(-24, -50);
    ctx.fill();

    // Nose (Small button)
    ctx.fillStyle = "#d35400";
    ctx.globalAlpha = 0.25;
    ctx.beginPath();
    ctx.arc(0, 2, 1.8, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1.0;

    // SMIRK (Characteristic Asymmetrical Line from image)
    ctx.strokeStyle = "#3e2723";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(-8, 12);
    ctx.quadraticCurveTo(0, 14, 10, 10); // Subtle curve up on the right
    ctx.stroke();

    // Mustache Bits
    ctx.fillStyle = "#1a1311";
    ctx.beginPath(); ctx.arc(-11, 8, 1.2, 0, Math.PI*2); ctx.fill();
    ctx.beginPath(); ctx.arc(11, 8, 1.2, 0, Math.PI*2); ctx.fill();
    // Chin Patch
    ctx.globalAlpha = 0.4;
    ctx.beginPath(); ctx.ellipse(0, 18, 5, 2, 0, 0, Math.PI*2); ctx.fill();
    ctx.globalAlpha = 1.0;

    // Large Expressive Eyes
    const drawBobbyEye = (x: number, side: number) => {
        ctx.save();
        ctx.translate(x, -12);
        
        // Sclera
        ctx.fillStyle = "#fff";
        ctx.beginPath();
        ctx.ellipse(0, 0, 8, 6.5, 0.05 * side, 0, Math.PI*2);
        ctx.fill();
        
        // Iris/Pupil (Dark Brown)
        ctx.fillStyle = "#3e2723";
        ctx.beginPath();
        ctx.arc(side * 0.5, -0.5, 4.5, 0, Math.PI*2);
        ctx.fill();
        
        // Inner Pupil
        ctx.fillStyle = "#1a0f08";
        ctx.beginPath();
        ctx.arc(side * 0.5, -0.5, 2, 0, Math.PI*2);
        ctx.fill();

        // Highlights
        ctx.fillStyle = "#fff"; 
        ctx.beginPath(); ctx.arc(2, -2.5, 1.8, 0, Math.PI*2); ctx.fill();
        ctx.beginPath(); ctx.arc(-1.5, 1.5, 0.8, 0, Math.PI*2); ctx.fill();
        ctx.restore();
    };
    drawBobbyEye(-10, -1);
    drawBobbyEye(10, 1);

    // Thick Expressive Eyebrows
    ctx.strokeStyle = "#1a1311";
    ctx.lineWidth = 4.5;
    ctx.lineCap = "round";
    // Left
    ctx.beginPath();
    ctx.moveTo(-18, -25);
    ctx.quadraticCurveTo(-12, -32, -4, -26);
    ctx.stroke();
    // Right (Higher arch like image)
    ctx.beginPath();
    ctx.moveTo(4, -26);
    ctx.quadraticCurveTo(12, -34, 18, -27);
    ctx.stroke();

    // --- 7. HAIR (MATCHING WAVE & STRANDS FROM REFERENCE) ---
    ctx.save();
    ctx.fillStyle = HAIR_COLOR;
    
    // Back hair mass
    ctx.beginPath();
    ctx.ellipse(0, -30, 29, 34, 0, Math.PI, 0);
    ctx.fill();

    // Wavy sides
    const drawWavySide = (side: number) => {
        ctx.beginPath();
        ctx.moveTo(side * 28, -30);
        ctx.quadraticCurveTo(side * 36, -10, side * 30, 8);
        ctx.quadraticCurveTo(side * 25, 18, side * 18, 5);
        ctx.fill();
    };
    drawWavySide(-1);
    drawWavySide(1);

    // FRONT HAIRLINE (THE "M" SHAPED WAVE)
    ctx.beginPath();
    ctx.moveTo(-25, -45); 
    ctx.quadraticCurveTo(-18, -52, -12, -42);
    ctx.quadraticCurveTo(0, -58, 12, -42);
    ctx.quadraticCurveTo(18, -52, 25, -45);
    ctx.quadraticCurveTo(15, -65, 0, -62);
    ctx.quadraticCurveTo(-15, -65, -25, -45);
    ctx.fill();

    // FRONT STRANDS
    const drawBobbyStrand = (sx: number, sy: number, ex: number, ey: number, cp: number, thick: number) => {
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.quadraticCurveTo(cp, sy + 18, ex, ey);
        ctx.lineWidth = thick;
        ctx.strokeStyle = HAIR_COLOR;
        ctx.lineCap = "round";
        ctx.stroke();
    };
    drawBobbyStrand(-14, -48, -18, -28, -22, 5); 
    drawBobbyStrand(8, -48, 14, -30, 16, 4.5);     

    // Extra volume strokes
    ctx.strokeStyle = HAIR_COLOR;
    ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(-10, -58); ctx.quadraticCurveTo(0, -68, 10, -58); ctx.stroke();
    
    ctx.restore();

    ctx.restore();
};
